export type Language = "en" | "es";

export type FocusArea = {
  title: string;
  description: string;
};

export type PageContent = {
  htmlLang: string;
  headerLabel: string;
  languageLabel: string;
  eyebrow: string;
  title: string;
  intro: string;
  purposeLabel: string;
  purposeTitle: string;
  purposeText: string;
  focusLabel: string;
  focusTitle: string;
  focusAreas: FocusArea[];
  footerTagline: string[];
  footerNote: string[];
};

export const content: Record<Language, PageContent> = {
  en: {
    htmlLang: "en",
    headerLabel: "Site header",
    languageLabel: "Language selector",
    eyebrow: "Professional technology portal",
    title: "Technology ideas for practical solutions.",
    intro:
      "A professional space focused on exploring technology ideas, practical approaches, and open collaboration around real needs across business, social, and open-source contexts.",
    purposeLabel: "Purpose",
    purposeTitle: "Connecting technical knowledge, innovation, and execution.",
    purposeText:
      "The goal of peyotl.mx is to transform ideas into useful digital solutions with local and global relevance.",
    focusLabel: "Focus areas",
    focusTitle: "Ideas, research, social impact, and collaboration.",
    focusAreas: [
      {
        title: "Technology ideas",
        description: "Practical concepts that turn emerging technology into useful solutions.",
      },
      {
        title: "Research and experimentation",
        description: "Focused exploration, prototypes, and learning through real-world testing.",
      },
      {
        title: "Business and community challenges",
        description: "Digital approaches shaped around concrete organizational and local needs.",
      },
      {
        title: "Social impact initiatives",
        description: "Technology projects designed to create meaningful and measurable benefits.",
      },
      {
        title: "Open-source collaboration",
        description: "Shared tools and knowledge built transparently with the wider community.",
      },
      {
        title: "Local and global context",
        description: "Solutions grounded in local realities and informed by global perspectives.",
      },
    ],
    footerTagline: ["DIFFERENT PERSPECTIVES.", "REAL SOLUTIONS."],
    footerNote: ["IDEAS TODAY", "FOR A BETTER TOMORROW."],
  },
  es: {
    htmlLang: "es",
    headerLabel: "Encabezado del sitio",
    languageLabel: "Selector de idioma",
    eyebrow: "Portal profesional de tecnología",
    title: "Ideas tecnológicas para soluciones prácticas.",
    intro:
      "Un espacio profesional enfocado en explorar ideas tecnológicas, enfoques prácticos y colaboración abierta alrededor de necesidades reales en contextos empresariales, sociales y de código abierto.",
    purposeLabel: "Propósito",
    purposeTitle: "Conectar conocimiento técnico, innovación y ejecución.",
    purposeText:
      "El objetivo de peyotl.mx es transformar ideas en soluciones digitales útiles con relevancia local y global.",
    focusLabel: "Áreas de enfoque",
    focusTitle: "Ideas, investigación, impacto social y colaboración.",
    focusAreas: [
      {
        title: "Ideas tecnológicas",
        description: "Conceptos prácticos que convierten tecnología emergente en soluciones útiles.",
      },
      {
        title: "Investigación y experimentación",
        description: "Exploración enfocada, prototipos y aprendizaje mediante pruebas reales.",
      },
      {
        title: "Retos empresariales y comunitarios",
        description: "Enfoques digitales creados para necesidades organizacionales y locales concretas.",
      },
      {
        title: "Iniciativas de impacto social",
        description: "Proyectos tecnológicos diseñados para generar beneficios significativos y medibles.",
      },
      {
        title: "Colaboración de código abierto",
        description: "Herramientas y conocimiento compartidos de forma transparente con la comunidad.",
      },
      {
        title: "Contexto local y global",
        description: "Soluciones basadas en realidades locales e informadas por perspectivas globales.",
      },
    ],
    footerTagline: ["DIFERENTES PERSPECTIVAS.", "SOLUCIONES REALES."],
    footerNote: ["IDEAS DE HOY", "PARA UN MAÑANA MEJOR."],
  },
};

export function getBrowserLanguage(): Language {
  const browserLanguages =
    typeof navigator === "undefined"
      ? []
      : navigator.languages?.length
      ? navigator.languages
      : [navigator.language];

  const preferredLanguage = browserLanguages.find((browserLanguage) => {
    const baseLanguage = browserLanguage.toLowerCase().split("-")[0];
    return baseLanguage === "en" || baseLanguage === "es";
  });

  return preferredLanguage?.toLowerCase().split("-")[0] === "es" ? "es" : "en";
}
